import classnames from 'classnames'
import { FormikContext, useFormik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import ArrowBackIcon from '@material-symbols/svg-300/rounded/arrow_back.svg'
import { useBranding } from '../../branding'

import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import setFormErrors from '../../utilities/setFormErrors'
import useDialog from '../../utilities/useDialog'
import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'
import Button from '../basics/Button/Button'
import { TextInputFormik } from '../basics/TextInput/TextInput'
import * as api from '../../state/api'
import SkillfitLogo from '../SkillfitLogo/SkillfitLogo'

import styles from './LoginForm.module.scss'

/**********************************************************************
 * Login
 **********************************************************************/
const Login = React.memo(function Login({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation()
  const { invalidate } = useSession()
  const routes = useRoutes()
  const location = useLocation()

  const { clientCode } = useBranding()

  const [isBusy, setBusy] = useState(false)

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    onSubmit: async (values, { setErrors, setStatus }) => {
      setBusy(true)
      setStatus()
      const { email, password } = values

      // Allow users to log in using only their username without '#CLIENT' at
      // the end on branded instances.
      const effectiveUsername = clientCode && !email.includes('@') ? `${email}#${clientCode}` : email

      try {
        const result = await api.post('login', { email: effectiveUsername, password })

        if (result.status === 'fail') {
          throw new Error(result.message)
        }
        invalidate()
      } catch (err: any) {
        if (err?.code === 403) {
          setStatus(t('errors.login failed'))
        } else {
          setFormErrors({ setErrors, setStatus, t }, err)
        }
        setBusy(false)
      }
    },
  })

  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    formik.resetForm()
    if (isActive) {
      // We set a timeout here because we have to wait until the form has
      // scrolled into view. Otherwise, the whole layout breaks.
      setTimeout(() => formRef.current && formRef.current.querySelector('input')?.focus(), 300)
    }
  }, [isActive, formik.resetForm])

  return (
    <FormikContext.Provider value={formik}>
      <form
        ref={formRef}
        noValidate
        onSubmit={formik.handleSubmit}
        {...{ inert: !isActive ? '' : undefined }}
        aria-hidden={!isActive}
      >
        <h1>{t('login password.loginTitle')}</h1>
        <TextInputFormik
          label={t('login password.field username or email')}
          name="email"
          autoComplete="email"
          required
          disabled={isBusy}
        />
        <TextInputFormik
          label={t('login password.password')}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          disabled={isBusy}
        />
        <Link to={routes.current.withQuery(location.search, { requestPasswordReset: 'true' }).url({})}>
          {t('login password.forgot your password?')}
        </Link>
        {formik.status && <p className={styles.errorMessage}>{formik.status}</p>}
        <Button type="submit" filled loading={isBusy} disabled={isBusy}>
          {t('login password.login')}
        </Button>
      </form>
    </FormikContext.Provider>
  )
})

/**********************************************************************
 * RequestPasswordReset
 **********************************************************************/
const RequestPasswordReset = React.memo(function RequestPasswordReset({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const location = useLocation()
  const showDialog = useDialog()

  const formik = useFormik({
    initialValues: { email: '' },
    onSubmit: async (values, { setStatus, resetForm }) => {
      setStatus()

      if (!values.email.includes('@')) {
        await showDialog((close) => (
          <div className={styles.requesetPasswordResetSuccessMessage}>
            <p>{t('login password.contact admin for password reset')}</p>
            <Button onClick={close}>{t('generic cta.ok')}</Button>
          </div>
        ))
      } else {
        try {
          await api.post('password-forget', values)

          resetForm()

          await showDialog((close) => (
            <div className={styles.requesetPasswordResetSuccessMessage}>
              <p>{t('login password.Password reset email sent')}</p>
              <Button onClick={close}>{t('generic cta.ok')}</Button>
            </div>
          ))

          routes.current.withQuery(location.search, { requestPasswordReset: null }).goto({})
        } catch (err) {
          setStatus(String(err))
        }
      }
    },
  })

  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    formik.resetForm()
    if (isActive) {
      // We set a timeout here because we have to wait until the form has
      // scrolled into view. Otherwise, the whole layout breaks.
      setTimeout(() => formRef.current && formRef.current.querySelector('input')?.focus(), 300)
    }
  }, [isActive, formik.resetForm])

  return (
    <FormikContext.Provider value={formik}>
      <form
        ref={formRef}
        noValidate
        onSubmit={formik.handleSubmit}
        {...{ inert: !isActive ? '' : undefined }}
        aria-hidden={!isActive}
      >
        <div className={styles.header}>
          <h1>{t('login password.reset password')}</h1>
          <Button
            small
            component={Link}
            to={routes.current.withQuery(location.search, { requestPasswordReset: null }).url({})}
            aria-label={t('generic cta.back')}
          >
            <ArrowBackIcon />
          </Button>
        </div>
        <TextInputFormik
          label={t('login password.field username or email')}
          type="text"
          name="email"
          autoComplete="email"
          required
          disabled={formik.isSubmitting}
        />
        {formik.status && <p className={styles.errorMessage}>{formik.status}</p>}
        <Button type="submit" filled loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {t('login password.request password reset')}
        </Button>
      </form>
    </FormikContext.Provider>
  )
})

/**********************************************************************
 * LoginForm
 **********************************************************************/
const loginFormParams = queryParameters().boolean('requestPasswordReset')

export default React.memo(function LoginForm() {
  const { requestPasswordReset } = useQueryParameters(loginFormParams)

  return (
    <div className={styles.LoginForm}>
      <SkillfitLogo className={styles.logo} />
      <div className={classnames(styles.forms, { [styles.requestPasswordResetActive]: requestPasswordReset })}>
        <Login isActive={!requestPasswordReset} />
        <RequestPasswordReset isActive={requestPasswordReset} />
      </div>
    </div>
  )
})
